/**
 * Gestion des cartes (design system, EcranGestionDesCartes, Tableau ; spécification §8) :
 * chercher, parcourir, ouvrir, déplacer, suspendre, réactiver et supprimer les cartes.
 * Une ligne par carte fille : chacune a son propre suivi.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import type { CarteMere } from '../../cartes/carte-mere';
import { normaliser, questionDeLaCarte, texteDeLaCarte } from '../../contenu/texte';
import { State } from '../../fsrs/planificateur';
import type { CarteLue } from '../../supabase/conversions';
import { Bouton } from '../composants/Bouton';
import { Chargement } from '../composants/Chargement';
import { Dialogue } from '../composants/Dialogue';
import { EtatVide } from '../composants/EtatVide';
import { Icone } from '../composants/Icone';
import { ListeDeroulante } from '../composants/ListeDeroulante';
import { Menu, type Ancre } from '../composants/Menu';
import { useNotifier } from '../composants/Notifications';
import { Touche } from '../composants/Touche';
import { Coquille } from '../Coquille';
import { aplatir, construireArbre } from '../donnees/arbre';
import { useDonnees } from '../donnees/contexte';
import { useImages } from '../donnees/images';
import { messageErreur } from '../donnees/erreurs';
import { dateCourte, prochaineRevision } from '../format';
import { cx, dansUnChamp, pluriel } from '../outils';
import { naviguer } from '../routes';

type Etat = 'nouvelle' | 'apprentissage' | 'acquise' | 'suspendue';
type Colonne = 'question' | 'paquet' | 'etat' | 'revision' | 'ajout';
interface Tri {
  colonne: Colonne;
  sens: 'ascending' | 'descending';
}

interface Ligne {
  carte: CarteLue;
  carteMere: CarteMere;
  question: string;
  etat: Etat;
  recherche: string;
}

const ETATS: { etat: Etat; libelle: string; pastille: string }[] = [
  { etat: 'nouvelle', libelle: 'Nouvelles cartes', pastille: 'Nouvelle' },
  { etat: 'apprentissage', libelle: 'En apprentissage', pastille: 'Apprentissage' },
  { etat: 'acquise', libelle: 'Acquises', pastille: 'Acquise' },
  { etat: 'suspendue', libelle: 'Suspendues', pastille: 'Suspendue' },
];
const ETATS_PAR_DEFAUT: Etat[] = ['nouvelle', 'apprentissage', 'acquise'];
const RANG_ETAT: Record<Etat, number> = { nouvelle: 0, apprentissage: 1, acquise: 2, suspendue: 3 };

function etatDe(carte: CarteLue): Etat {
  if (carte.suspendue) return 'suspendue';
  if (carte.fsrs.state === State.New) return 'nouvelle';
  if (carte.fsrs.state === State.Review) return 'acquise';
  return 'apprentissage';
}

function Pastille({ etat }: { etat: Etat }) {
  return (
    <span className={`fc-pastille fc-pastille--${etat}`}>
      {etat === 'suspendue' && <Icone nom="circle-pause" />}
      {ETATS.find((e) => e.etat === etat)!.pastille}
    </span>
  );
}

/** Vignette de l'image d'une carte de masquage d'image, devant sa question. */
function Miniature({
  chemin,
  resoudre,
}: {
  chemin: string;
  resoudre: (chemin: string) => Promise<string>;
}) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let actif = true;
    void resoudre(chemin)
      .then((s) => actif && setSrc(s))
      .catch(() => undefined);
    return () => {
      actif = false;
    };
  }, [chemin, resoudre]);
  return <span className="fc-tableau__miniature">{src && <img src={src} alt="" />}</span>;
}

function CheminCourt({ chemin }: { chemin: string[] }) {
  const affiche = chemin.length > 2 ? [chemin[0]!, '…', chemin.at(-1)!] : chemin;
  return (
    <span className="fc-tableau__paquet" title={chemin.join(' › ')}>
      {affiche.map((nom, i) => (
        <span key={i}>{nom}</span>
      ))}
    </span>
  );
}

export function EcranGestionDesCartes({
  paquetFiltre,
  rechercheInitiale = '',
}: {
  paquetFiltre?: string;
  rechercheInitiale?: string;
}) {
  const { depot, paquets, rechargerJours } = useDonnees();
  const images = useImages();
  const notifier = useNotifier();
  const [lignes, setLignes] = useState<Ligne[] | null>(null);
  const [recherche, setRecherche] = useState(rechercheInitiale);
  const [paquet, setPaquet] = useState<string>(paquetFiltre ?? 'tous');
  const [etats, setEtats] = useState<Etat[]>(ETATS_PAR_DEFAUT);
  const [tri, setTri] = useState<Tri>({ colonne: 'ajout', sens: 'descending' });
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [ancreSelection, setAncreSelection] = useState<string | null>(null);
  const [actif, setActif] = useState<string | null>(null);
  const [menuEtats, setMenuEtats] = useState<Ancre | null>(null);
  const [dialogue, setDialogue] = useState<'supprimer' | 'deplacer' | null>(null);
  const [destination, setDestination] = useState<string | null>(null);
  const saisie = useRef<HTMLInputElement>(null);
  const grille = useRef<HTMLTableElement>(null);
  const noeuds = useMemo(() => aplatir(construireArbre(paquets ?? [])), [paquets]);

  const charger = useCallback(async () => {
    if (paquets === null) return;
    try {
      const [meres, ...parPaquet] = await Promise.all([
        depot.cartesMeres(),
        ...paquets.filter((p) => p.parentId === null).map((p) => depot.cartesDuPaquet(p.id)),
      ]);
      const indexMeres = new Map(meres.map((m) => [m.id, m]));
      const resultat: Ligne[] = [];
      for (const carte of parPaquet.flat()) {
        const mere = indexMeres.get(carte.carteMereId);
        if (!mere) continue;
        const question = questionDeLaCarte(mere.carte, carte.cle);
        resultat.push({
          carte,
          carteMere: mere.carte,
          question,
          etat: etatDe(carte),
          recherche: normaliser(
            `${question} ${texteDeLaCarte(mere.carte)} ${carte.chemin.join(' ')}`,
          ),
        });
      }
      setLignes(resultat);
    } catch (e) {
      notifier({ message: messageErreur(e), statut: 'danger' });
      setLignes([]);
    }
  }, [depot, paquets, notifier]);

  useEffect(() => {
    void charger();
  }, [charger]);

  // « / » ramène à la recherche.
  useEffect(() => {
    const clavier = (e: globalThis.KeyboardEvent) => {
      if (
        e.key === '/' &&
        !dansUnChamp(e.target) &&
        !e.metaKey &&
        !e.ctrlKey &&
        !document.querySelector('.fc-dialogue__voile')
      ) {
        e.preventDefault();
        saisie.current?.focus();
      }
    };
    window.addEventListener('keydown', clavier);
    return () => window.removeEventListener('keydown', clavier);
  }, []);

  const paquetsFiltres = useMemo(() => {
    if (paquet === 'tous') return null;
    const ids = new Set<string>([paquet]);
    for (const n of noeuds)
      if (n.paquet.parentId && ids.has(n.paquet.parentId)) ids.add(n.paquet.id);
    return ids;
  }, [paquet, noeuds]);

  const visibles = useMemo(() => {
    if (!lignes) return [];
    const termes = normaliser(recherche).split(/\s+/).filter(Boolean);
    const filtrees = lignes.filter(
      (l) =>
        etats.includes(l.etat) &&
        (paquetsFiltres === null || paquetsFiltres.has(l.carte.paquetId)) &&
        termes.every((t) => l.recherche.includes(t)),
    );
    const facteur = tri.sens === 'ascending' ? 1 : -1;
    const revision = (l: Ligne) =>
      l.etat === 'nouvelle' || l.etat === 'suspendue' ? Infinity : l.carte.fsrs.due.getTime();
    const comparer: Record<Colonne, (a: Ligne, b: Ligne) => number> = {
      question: (a, b) => a.question.localeCompare(b.question, 'fr', { sensitivity: 'base' }),
      paquet: (a, b) => a.carte.chemin.join('›').localeCompare(b.carte.chemin.join('›'), 'fr'),
      etat: (a, b) => RANG_ETAT[a.etat] - RANG_ETAT[b.etat],
      revision: (a, b) => revision(a) - revision(b),
      ajout: (a, b) =>
        a.carte.creeLe.getTime() - b.carte.creeLe.getTime() || a.carte.rang - b.carte.rang,
    };
    return [...filtrees].sort(
      (a, b) => facteur * comparer[tri.colonne](a, b) || comparer.ajout(a, b),
    );
  }, [lignes, recherche, etats, paquetsFiltres, tri]);

  const choisies = visibles.filter((l) => selection.has(l.carte.id));
  const toutesSuspendues = choisies.length > 0 && choisies.every((l) => l.carte.suspendue);

  function selectionner(
    id: string,
    e: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean },
    basculer = true,
  ) {
    setActif(id);
    const suivante = new Set(selection);
    if (e.shiftKey && ancreSelection) {
      const a = visibles.findIndex((l) => l.carte.id === ancreSelection);
      const b = visibles.findIndex((l) => l.carte.id === id);
      for (const l of visibles.slice(Math.min(a, b), Math.max(a, b) + 1)) suivante.add(l.carte.id);
    } else if (basculer) {
      if (suivante.has(id)) suivante.delete(id);
      else suivante.add(id);
      setAncreSelection(id);
    }
    setSelection(suivante);
  }

  function ouvrir(ligne: Ligne) {
    naviguer({ ecran: 'editeur', carteMere: ligne.carte.carteMereId });
  }

  function focaliser(id: string) {
    setActif(id);
    grille.current?.querySelector<HTMLElement>(`[data-id="${CSS.escape(id)}"]`)?.focus();
  }

  function clavierLigne(e: KeyboardEvent<HTMLTableRowElement>, ligne: Ligne, i: number) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const suivante = visibles[e.key === 'ArrowDown' ? i + 1 : i - 1];
      if (!suivante) return;
      e.preventDefault();
      if (e.shiftKey) {
        const nouvelle = new Set(selection).add(ligne.carte.id).add(suivante.carte.id);
        setSelection(nouvelle);
      }
      focaliser(suivante.carte.id);
    } else if (e.key === ' ') {
      e.preventDefault();
      selectionner(ligne.carte.id, { shiftKey: false, metaKey: false, ctrlKey: false });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      ouvrir(ligne);
    } else if (e.key === 'a' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      setSelection(new Set(visibles.map((l) => l.carte.id)));
    } else if (e.key === 'Escape') {
      setSelection(new Set());
    }
  }

  async function suspendreSelection(suspendue: boolean) {
    try {
      await Promise.all(choisies.map((l) => depot.suspendre(l.carte.id, suspendue)));
      notifier({
        message: suspendue
          ? `${pluriel(choisies.length, 'carte suspendue', 'cartes suspendues')}.`
          : `${pluriel(choisies.length, 'carte réactivée', 'cartes réactivées')}.`,
        statut: 'succes',
      });
      setSelection(new Set());
      void rechargerJours();
      await charger();
    } catch (e) {
      notifier({ message: messageErreur(e), statut: 'danger' });
    }
  }

  const meresChoisies = [...new Set(choisies.map((l) => l.carte.carteMereId))];
  const fillesConcernees = lignes
    ? lignes.filter((l) => meresChoisies.includes(l.carte.carteMereId)).length
    : 0;

  const enTete = (colonne: Colonne, libelle: string, nombre = false) => (
    <th
      className={cx('fc-tableau__entete', nombre && 'fc-tableau__entete--nombre')}
      scope="col"
      aria-sort={tri.colonne === colonne ? tri.sens : 'none'}
    >
      <button
        type="button"
        className="fc-tableau__tri"
        onClick={() =>
          setTri(
            tri.colonne === colonne
              ? { colonne, sens: tri.sens === 'ascending' ? 'descending' : 'ascending' }
              : { colonne, sens: 'ascending' },
          )
        }
      >
        {libelle}
        <Icone nom="arrow-up-down" className="fc-tableau__tri-indice" />
        <Icone nom="chevron-down" className="fc-tableau__tri-sens" />
      </button>
    </th>
  );

  const libelleEtats =
    etats.length === 4
      ? 'État : toutes'
      : etats.length === 3 && !etats.includes('suspendue')
        ? 'État : sauf suspendues'
        : `État : ${etats.length}`;

  return (
    <Coquille
      titre="Gestion des cartes"
      colonne="large"
      actions={
        <Bouton
          variante="discret"
          taille="compacte"
          icone="plus"
          className="fc-seulement-sans-laterale"
          onClick={() =>
            naviguer(paquet !== 'tous' ? { ecran: 'editeur', paquet } : { ecran: 'editeur' })
          }
        >
          Nouvelle carte
        </Bouton>
      }
    >
      <div className="fc-ecran-gestion">
        <div className="fc-fenetre__entete">
          <h2 className="fc-fenetre__titre-ecran">Gestion des cartes</h2>
        </div>
        <div className="fc-ecran-gestion__filtres">
          <div className="fc-champ fc-champ-recherche">
            <Icone nom="search" className="fc-champ__icone" />
            <input
              ref={saisie}
              className="fc-champ__saisie"
              type="search"
              placeholder="Rechercher des cartes"
              aria-label="Rechercher des cartes"
              aria-keyshortcuts="/"
              aria-controls="gestion-cartes"
              autoComplete="off"
              spellCheck={false}
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  if (recherche) setRecherche('');
                  else grille.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
                }
              }}
            />
            {/* Le nombre est déjà affiché à droite des filtres : ici, il n'est qu'annoncé. */}
            <span className="fc-sr" role="status">
              {recherche
                ? visibles.length === 0
                  ? 'Aucune carte'
                  : pluriel(visibles.length, 'carte')
                : ''}
            </span>
            <Touche className="fc-champ-recherche__touche">/</Touche>
            <button
              type="button"
              className="fc-champ__bouton fc-champ-recherche__effacer"
              aria-label="Effacer la recherche"
              title="Effacer la recherche"
              onClick={() => {
                setRecherche('');
                saisie.current?.focus();
              }}
            >
              <Icone nom="x" />
            </button>
          </div>
          <ListeDeroulante
            etiquette="Paquet"
            options={[
              { valeur: 'tous', libelle: 'Tous les paquets', icone: 'folder' },
              ...noeuds.map((n) => ({
                valeur: n.paquet.id,
                libelle: n.paquet.nom,
                icone: n.niveau === 0 ? ('layers' as const) : ('folder' as const),
                niveau: n.niveau,
              })),
            ]}
            valeur={paquet}
            onChoisir={(v) => {
              setPaquet(v);
              setSelection(new Set());
            }}
          />
          <div className="fc-ecran-gestion__filtre">
            <button
              type="button"
              className="fc-bouton fc-bouton--secondaire fc-bouton--compact"
              aria-haspopup="menu"
              aria-expanded={menuEtats !== null}
              onClick={(e) => setMenuEtats(menuEtats ? null : e.currentTarget)}
            >
              <Icone nom="list-filter" />
              {libelleEtats}
              <Icone nom="chevron-down" />
            </button>
          </div>
          <p className="fc-ecran-gestion__compte">
            {lignes
              ? visibles.length === 0
                ? 'Aucune carte'
                : pluriel(visibles.length, 'carte')
              : ''}
          </p>
        </div>

        {lignes === null ? (
          <Chargement type="rotation" texte="Chargement des cartes…" centre />
        ) : visibles.length === 0 ? (
          <div className="fc-ecran-gestion__vide">
            {lignes.length === 0 ? (
              <EtatVide
                icone="layers"
                titre="Aucune carte"
                texte="Les cartes que vous écrivez apparaissent ici."
                action={
                  <Bouton
                    variante="principal"
                    icone="plus"
                    onClick={() => naviguer({ ecran: 'editeur' })}
                  >
                    Nouvelle carte
                  </Bouton>
                }
              />
            ) : recherche ? (
              <EtatVide
                icone="search"
                annonce
                titre={`Aucune carte ne correspond à « ${recherche} »`}
                action={<Bouton onClick={() => setRecherche('')}>Effacer la recherche</Bouton>}
              />
            ) : (
              <EtatVide
                icone="list-filter"
                annonce
                titre="Aucune carte avec ces filtres"
                action={
                  <Bouton
                    onClick={() => {
                      setEtats(ETATS_PAR_DEFAUT);
                      setPaquet('tous');
                    }}
                  >
                    Rétablir les filtres
                  </Bouton>
                }
              />
            )}
          </div>
        ) : (
          <div className={cx('fc-tableau', selection.size > 0 && 'fc-tableau--selection')}>
            <div className="fc-tableau__defilement">
              <table
                ref={grille}
                className="fc-tableau__grille"
                role="grid"
                id="gestion-cartes"
                aria-label="Cartes"
                aria-multiselectable="true"
              >
                <colgroup>
                  <col className="fc-tableau__col-case" />
                  <col />
                  <col className="fc-tableau__col-paquet" />
                  <col className="fc-tableau__col-etat" />
                  <col className="fc-tableau__col-revision" />
                  <col className="fc-tableau__col-ajout" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="fc-tableau__entete fc-tableau__entete--case" scope="col">
                      <button
                        type="button"
                        className="fc-tableau__case"
                        role="checkbox"
                        aria-checked={
                          choisies.length === 0
                            ? false
                            : choisies.length === visibles.length
                              ? true
                              : 'mixed'
                        }
                        aria-label="Sélectionner toutes les cartes"
                        onClick={() =>
                          setSelection(
                            choisies.length === visibles.length
                              ? new Set()
                              : new Set(visibles.map((l) => l.carte.id)),
                          )
                        }
                      >
                        <Icone nom="check" className="fc-tableau__coche" />
                        <Icone nom="minus" className="fc-tableau__tiret" />
                      </button>
                    </th>
                    {enTete('question', 'Question')}
                    {enTete('paquet', 'Paquet')}
                    {enTete('etat', 'État')}
                    {enTete('revision', 'Prochaine révision', true)}
                    {enTete('ajout', 'Ajoutée le', true)}
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((ligne, i) => {
                    const id = ligne.carte.id;
                    const prochaine =
                      ligne.etat === 'nouvelle' || ligne.etat === 'suspendue'
                        ? null
                        : prochaineRevision(ligne.carte.fsrs.due);
                    return (
                      <tr
                        key={id}
                        data-id={id}
                        className={cx(
                          'fc-tableau__ligne',
                          ligne.carte.suspendue && 'fc-tableau__ligne--suspendue',
                        )}
                        aria-selected={selection.has(id)}
                        tabIndex={(actif ?? visibles[0]?.carte.id) === id ? 0 : -1}
                        onClick={(e: MouseEvent) => {
                          if ((e.target as HTMLElement).closest('.fc-tableau__case')) return;
                          if (e.shiftKey || e.metaKey || e.ctrlKey) selectionner(id, e);
                          else setActif(id);
                        }}
                        onDoubleClick={() => ouvrir(ligne)}
                        onKeyDown={(e) => clavierLigne(e, ligne, i)}
                      >
                        <td className="fc-tableau__cellule fc-tableau__cellule--case">
                          <span
                            className="fc-tableau__case"
                            aria-hidden="true"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectionner(id, e);
                            }}
                          >
                            <Icone nom="check" className="fc-tableau__coche" />
                          </span>
                        </td>
                        <td
                          className="fc-tableau__cellule fc-tableau__cellule--question"
                          title={ligne.question}
                        >
                          {ligne.carteMere.type === 'image' && (
                            <Miniature
                              chemin={ligne.carteMere.contenu.image}
                              resoudre={images.resoudre}
                            />
                          )}
                          {ligne.question || '—'}
                        </td>
                        <td className="fc-tableau__cellule">
                          <CheminCourt chemin={ligne.carte.chemin} />
                        </td>
                        <td className="fc-tableau__cellule">
                          <Pastille etat={ligne.etat} />
                        </td>
                        <td
                          className="fc-tableau__cellule fc-tableau__cellule--nombre"
                          {...(prochaine ? {} : { 'aria-label': 'Aucune' })}
                        >
                          {prochaine ?? <span aria-hidden="true">—</span>}
                        </td>
                        <td className="fc-tableau__cellule fc-tableau__cellule--nombre">
                          {dateCourte(ligne.carte.creeLe)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {choisies.length > 0 && (
              <div
                className="fc-tableau__actions"
                role="toolbar"
                aria-label="Actions sur les cartes sélectionnées"
              >
                <span className="fc-tableau__compte" aria-live="polite">
                  {pluriel(choisies.length, 'carte sélectionnée', 'cartes sélectionnées')}
                </span>
                <Bouton
                  variante="discret"
                  taille="compacte"
                  iconeSeule
                  icone="x"
                  etiquette="Tout désélectionner"
                  title="Tout désélectionner (Échap)"
                  aria-keyshortcuts="Escape"
                  onClick={() => setSelection(new Set())}
                />
                <span className="fc-tableau__filet" aria-hidden="true" />
                <Bouton
                  variante="discret"
                  taille="compacte"
                  icone="folder-open"
                  onClick={() => {
                    setDestination(null);
                    setDialogue('deplacer');
                  }}
                >
                  Déplacer…
                </Bouton>
                <Bouton
                  variante="discret"
                  taille="compacte"
                  icone="circle-pause"
                  onClick={() => void suspendreSelection(!toutesSuspendues)}
                >
                  {toutesSuspendues ? 'Réactiver' : 'Suspendre'}
                </Bouton>
                <Bouton
                  variante="danger-discret"
                  taille="compacte"
                  icone="trash-2"
                  onClick={() => setDialogue('supprimer')}
                >
                  Supprimer
                </Bouton>
              </div>
            )}
          </div>
        )}
      </div>

      {menuEtats && (
        <Menu
          ancre={menuEtats}
          etiquette="Afficher les cartes"
          elements={[
            { type: 'titre', libelle: 'Afficher les cartes' },
            ...ETATS.map((e) => ({
              type: 'case' as const,
              libelle: e.libelle,
              coche: etats.includes(e.etat),
              onBasculer: (coche: boolean) =>
                setEtats(coche ? [...etats, e.etat] : etats.filter((x) => x !== e.etat)),
            })),
            { type: 'separateur' },
            {
              type: 'action',
              libelle: 'Rétablir les filtres',
              icone: 'rotate-ccw',
              onChoisir: () => setEtats(ETATS_PAR_DEFAUT),
            },
          ]}
          onFermer={() => setMenuEtats(null)}
        />
      )}

      {dialogue === 'supprimer' && (
        <Dialogue
          titre={
            meresChoisies.length === 1
              ? 'Supprimer la carte ?'
              : `Supprimer ${meresChoisies.length} cartes ?`
          }
          texte={`${
            fillesConcernees > meresChoisies.length
              ? `${pluriel(meresChoisies.length, 'carte écrite', 'cartes écrites')} et ${fillesConcernees >= 2 ? 'leurs' : 'sa'} ${pluriel(fillesConcernees, 'carte fille', 'cartes filles')}`
              : pluriel(meresChoisies.length, 'carte', 'cartes')
          }, avec leur historique de révision, seront supprimées. Cette action est définitive.`}
          actionPrincipale={{
            libelle: meresChoisies.length === 1 ? 'Supprimer la carte' : 'Supprimer les cartes',
            variante: 'danger',
            onAction: async () => {
              try {
                await Promise.all(meresChoisies.map((id) => depot.supprimerCarteMere(id)));
                notifier({
                  message:
                    meresChoisies.length === 1
                      ? 'Carte supprimée.'
                      : `${meresChoisies.length} cartes supprimées.`,
                  statut: 'succes',
                });
                setSelection(new Set());
                setDialogue(null);
                void rechargerJours();
                await charger();
              } catch (e) {
                notifier({ message: messageErreur(e), statut: 'danger' });
              }
            },
          }}
          onFermer={() => setDialogue(null)}
        />
      )}

      {dialogue === 'deplacer' && (
        <Dialogue
          titre={
            meresChoisies.length === 1
              ? 'Déplacer la carte'
              : `Déplacer ${meresChoisies.length} cartes`
          }
          texte={
            fillesConcernees > choisies.length
              ? 'Une carte se déplace avec toutes ses cartes filles. Leur suivi est conservé.'
              : 'Leur suivi est conservé.'
          }
          actionPrincipale={{
            libelle: 'Déplacer',
            desactive: destination === null,
            onAction: async () => {
              if (!destination) return;
              try {
                await Promise.all(
                  meresChoisies.map((id) => depot.deplacerCarteMere(id, destination)),
                );
                notifier({
                  message: `Déplacée${meresChoisies.length > 1 ? 's' : ''} dans ${noeuds.find((n) => n.paquet.id === destination)?.paquet.nom ?? ''}.`,
                  statut: 'succes',
                });
                setSelection(new Set());
                setDialogue(null);
                void rechargerJours();
                await charger();
              } catch (e) {
                notifier({ message: messageErreur(e), statut: 'danger' });
              }
            },
          }}
          onFermer={() => setDialogue(null)}
        >
          <span className="fc-champ-groupe__libelle" id="deplacer-destination">
            Paquet de destination
          </span>
          <ListeDeroulante
            libelleId="deplacer-destination"
            large
            exemple="Choisir un paquet"
            options={noeuds.map((n) => ({
              valeur: n.paquet.id,
              libelle: n.paquet.nom,
              icone: n.niveau === 0 ? ('layers' as const) : ('folder' as const),
              niveau: n.niveau,
            }))}
            affichage={(o) =>
              noeuds.find((n) => n.paquet.id === o.valeur)?.chemin.join(' › ') ?? o.libelle
            }
            valeur={destination}
            onChoisir={setDestination}
          />
        </Dialogue>
      )}
    </Coquille>
  );
}
